import React from "react";
import { ErrorMessage, Field, FieldProps, FormikProps } from "formik";
import { formErrorMessage } from "./FormErrorMessage";
import { CorporateTheme, CreateFormTheme, Theme } from "../../utils/themes";
import styled from "styled-components";
import { useTheme } from "../../middleware/ThemeContext";

interface FormikInputFieldProps {
  name: string;
  maxChar?: number;
  index?: number;
  isReadOnly?: boolean;
  isShowError?: boolean;
  id?: string;
  type?: string;
  as?: "input" | "textarea";
  rows?: number;
  placeholder?: string;
  bgcolor?: string;
  disabled?: boolean;
  rounded?: boolean;
  className?: string;
  radius?: string;
  padding?: string;
  min?: number;
  border?: string;
  ref?: any;
  value?: any;
  onChange?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  onBlur?: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => void;
  placeholderColor?: string; // NEW
  style?: React.CSSProperties;
  suffix?: string;
}

const FormikInputField: React.FC<FormikInputFieldProps> = ({
  name,
  maxChar,
  isReadOnly,
  isShowError,
  bgcolor,
  value,
  rounded,
  onBlur,
  onChange,
  as = "input",
  rows = 3,
  type = "text",
  min,
  ref,
  className = "",
  style,
  placeholderColor = "#6c757d",
  disabled,
  suffix,
  ...props
}) => {
  const { theme } = useTheme();
  return (
    <Field name={name} as={as}>
      {({
        field,
        form: { touched, errors, submitCount },
      }: FieldProps & { form: FormikProps<any>; meta: any }) => {
        const hasError = submitCount && touched[field.name] && errors[field.name];

        const commonClassNames = `
        form-control
        ${hasError ? "is-invalid" : ""}
        ${rounded ? "rounded-pill" : ""}
        ${bgcolor ? `bg-${bgcolor}` : ""}
        ${className}
        no-focus-border
      `;

        const placeholderStyle = {
          "::placeholder": {
            color: theme.CreateFormTheme.colors.placeHolder,
          } as React.CSSProperties,
        };

        const baseStyle: React.CSSProperties = {
          ...placeholderStyle,
          ...style,
          boxShadow: "none", // removes glow
          outline: "none", // removes default outline
          backgroundColor: theme.CreateFormTheme.colors.inputBackground,
          color: theme.CreateFormTheme.colors.placeHolder,
          borderColor:
            hasError
              ? theme.CreateFormTheme.colors.error
              : theme.CreateFormTheme?.colors?.inputBorders,
          opacity: disabled ? '0.5' : '1',
        };

        return (
          <div className="d-flex flex-column position-relative">
            {as === "textarea" ? (
              <StyledTextarea
                theme={theme}
                {...field}
                rows={rows}
                style={baseStyle}
                className={commonClassNames}
                id={props.id}
                readOnly={isReadOnly}
                onChange={(e) => {
                  field.onChange(e);
                  onChange?.(e);
                }}
                onBlur={(e) => {
                  field.onBlur(e);
                  onBlur?.(e);
                }}
                placeholder={props.placeholder}
                {...props}
              />
            ) : (
              <>
              <StyledInput
                theme={theme}
                ref={ref}
                type={type}
                min={min}
                maxLength={maxChar}
                disabled={disabled}
                autoComplete="off"
                {...field}
                style={baseStyle}
                className={commonClassNames}
                value={
                  isReadOnly ? "" : value !== undefined ? value : field.value
                }
                id={props.id}
                readOnly={isReadOnly}
                onChange={(e) => {
                  field.onChange(e);
                  onChange?.(e);
                }}
                onBlur={(e) => {
                  field.onBlur(e);
                  onBlur?.(e);
                }}
                placeholder={props.placeholder}
                {...props}
                $suffix={!!suffix}
              />
              {suffix && (
                <InputSuffix $theme={theme}>{suffix}</InputSuffix>
              )}
              </>
            )}

            {type !== "number" && maxChar && field.value && (
              <small className="form-text text-muted float-end">
                {field.value.length}/{maxChar}
              </small>
            )}

            {submitCount > 0 && !isShowError && (
              <ErrorMessage name={field.name} render={formErrorMessage} />
            )}
          </div>
        );
      }}
    </Field>
  );
};

const StyledInput = styled.input<{ $suffix?: boolean }>`
  font-family: "Raleway",sans-serif;
  font-size: 14px;
  padding: 9px 15px;
  ${({ $suffix }) => ($suffix  && 'padding-right: 60px')};
  height: 42px;
  &::placeholder {
    color: ${({ theme }) => theme.CreateFormTheme.colors.textSecondary};
  }
`;

const StyledTextarea = styled.textarea`
font-family: "Raleway",sans-serif;
  font-size: 14px;
  padding: 9px 15px;  
  &::placeholder {
    color: ${({ theme }) => theme.CreateFormTheme.colors.textSecondary};
  }
`;

const InputSuffix = styled.div<{ $theme: Theme }>`
    position: absolute;
    right: 1px;
    top: 1px;
    padding: 8px 12px;
    // background-color: #a2a6aa;
    border-radius: 0px 5px 5px 0px;
    background-color: ${({ $theme }) => $theme.CorporateTheme.colors.filterBG};
    color: ${({ $theme }) => $theme.CorporateTheme.colors.textSecondary};
`;

export default React.memo(FormikInputField);
